import axios, { AxiosHeaders } from 'axios'

const api = axios.create({
  // same-origin via NGINX/CDN
  baseURL: '',
  withCredentials: true,
})

const ANON_PATHS = [
  /^\/api\/auth\/2fa\/(verify|challenge|enabled-for-user)\/?$/,
  /^\/api\/auth\/token\/?$/,
  /^\/api\/auth\/token\/refresh\/?$/,
  /^\/api\/auth\/login\/?$/,
  /^\/api\/auth\/google\/?$/,
  /^\/api\/auth\/google\/config\/?$/,
  /^\/api\/auth\/csrf\/?$/,
  /^\/api\/auth\/password-reset\/confirm\/?$/,
]

api.interceptors.request.use((config) => {
  function getCookie(name: string): string | null {
    try {
      if (typeof document === 'undefined') return null
      const m = document.cookie.match('(?:^|; )' + name.replace(/([.$?*|{}()\[\]\\\/\+^])/g, '\\$1') + '=([^;]*)')
      return m ? decodeURIComponent(m[1]) : null
    } catch {
      return null
    }
  }
  const stripAuth = (h: any) => {
    try {
      if (!h) return
      if (typeof h.set === 'function') {
        try { h.set('Authorization', undefined as any) } catch {}
      }
      try { delete h.Authorization } catch {}
      try { delete h.authorization } catch {}
      if (h.common) {
        try { delete h.common.Authorization } catch {}
        try { delete h.common.authorization } catch {}
      }
    } catch {}
  }
  let path = ''
  try {
    const base = (config.baseURL as string) || ''
    const url = (config.url as string) || ''
    const abs = new URL(url, base || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost'))
    path = abs.pathname
  } catch {
    try { path = String(config.url || '') } catch { path = '' }
  }

  // Only strip Authorization on anonymous endpoints; leave JWT for others
  const isAnon = ANON_PATHS.some((rx) => {
    try { return rx.test(path) } catch { return false }
  })
  if (isAnon) {
    stripAuth(config.headers)
  }

  // Add CSRF token for unsafe methods (Django SessionAuthentication)
  try {
    const method = String(config.method || 'get').toUpperCase()
    const unsafe = !['GET', 'HEAD', 'OPTIONS', 'TRACE'].includes(method)
    if (!unsafe) {
      return config
    }
    const ensureHeader = (token: string | null) => {
      if (!token) return config
      const h: any = config.headers || {}
      if (typeof h.set === 'function') {
        try { h.set('X-CSRFToken', token) } catch { h['X-CSRFToken'] = token }
      } else {
        h['X-CSRFToken'] = token
      }
      config.headers = h
      return config
    }
    let csrf = getCookie('csrftoken')
    if (csrf) {
      return ensureHeader(csrf)
    }
    // If missing, prime it synchronously then attach
    if (typeof window !== 'undefined') {
      return new Promise((resolve) => {
        fetch('/api/auth/csrf', { method: 'GET', credentials: 'include' })
          .catch(() => undefined)
          .finally(() => {
            try { csrf = getCookie('csrftoken') } catch { csrf = null }
            resolve(ensureHeader(csrf))
          })
      }) as any
    }
  } catch {}
  return config
})

let isRefreshing = false
let pendingRequests: Array<(token: string | null) => void> = []

function processQueue(newToken: string | null) {
  pendingRequests.forEach((cb) => {
    try { cb(newToken) } catch {}
  })
  pendingRequests = []
}

api.interceptors.response.use(
  (r) => r,
  (error) => {
    if (typeof window !== 'undefined') {
      const cfg = error?.config || {}
      // Derive request path for filtering
      let path = ''
      try {
        const base = (cfg.baseURL as string) || ''
        const url = (cfg.url as string) || ''
        const abs = new URL(url, base || window.location.origin)
        path = abs.pathname
      } catch {
        try { path = String(cfg.url || '') } catch { path = '' }
      }
      const status = error?.response?.status
      const here = window.location?.pathname || ''
      const isPublicRoute = here === '/login' || here === '/twofa'
      const isMeEndpoint = /\/api\/auth\/me\/?$/.test(path)
      const isAuthNoise = isPublicRoute && isMeEndpoint && (status === 401 || status === 403)
      if (!isAuthNoise) {
        console.warn('[API ERROR]', {
          method: cfg.method,
          url: `${cfg.baseURL || ''}${cfg.url || ''}`,
          status: status,
          statusText: error?.response?.statusText,
        })
      }
    }
    // Handle 401 with refresh attempt
    const originalRequest = error?.config
    if (false && typeof window !== 'undefined' && error?.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true
      const refresh = localStorage.getItem('auth.refresh')
      if (!refresh) {
        localStorage.removeItem('auth.token')
        // Do not hard-redirect; allow the app to handle auth state via NextAuth
        return Promise.reject(error)
      }
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingRequests.push((newToken) => {
            if (newToken) {
              const oh: any = originalRequest.headers || {}
              if (typeof oh.set === 'function') {
                try { oh.set('Authorization', `Bearer ${newToken}`) } catch { oh.Authorization = `Bearer ${newToken}` }
              } else {
                oh.Authorization = `Bearer ${newToken}`
              }
              originalRequest.headers = oh
              resolve(api(originalRequest))
            } else {
              reject(error)
            }
          })
        })
      }
      isRefreshing = true
      return api.post('/api/auth/token/refresh/', { refresh })
        .then((res) => {
          const newToken: string | undefined = res?.data?.access
          const newRefresh: string | undefined = res?.data?.refresh
          if (newToken) {
            localStorage.setItem('auth.token', newToken)
            if (newRefresh) {
              // Rotate stored refresh token when server is configured to rotate
              localStorage.setItem('auth.refresh', newRefresh)
            }
            // Ensure subsequent requests (including parallel ones) use the fresh token
            try { (api.defaults.headers as any).common.Authorization = `Bearer ${newToken}` } catch {}
            processQueue(newToken)
            const oh2: any = originalRequest.headers || {}
            if (typeof oh2.set === 'function') {
              try { oh2.set('Authorization', `Bearer ${newToken}`) } catch { oh2.Authorization = `Bearer ${newToken}` }
            } else {
              oh2.Authorization = `Bearer ${newToken}`
            }
            originalRequest.headers = oh2
            return api(originalRequest)
          }
          throw new Error('No access token in refresh response')
        })
        .catch((e) => {
          processQueue(null)
          localStorage.removeItem('auth.token')
          localStorage.removeItem('auth.refresh')
          // Avoid hard redirects; let UI/auth layer decide next steps
          return Promise.reject(e)
        })
        .finally(() => {
          isRefreshing = false
        })
    }
    return Promise.reject(error)
  }
)

export { api }
